import {
  evaluateStudioPublishReadiness,
  readProjectDatasourceReadinessSidecar,
  withServerDatasourceReadinessProjection,
} from '@/services/project-api'
import { resolveStudioActiveProjectPlan } from '@/features/project/lib/studio-active-project-plan'
import { useStudioStore } from '@/store/studio-store'
import { PublishGateRows } from '@/shell/ide-v2/components/publish-gate-rows'
import { usePublishReadinessPublish } from '@/features/validation/use-publish-readiness-publish'
import { PublicationReleasePanel } from '@/features/publishing/publication-release-panel'
import { usePublicationRelease } from '@/features/publishing/use-publication-release'
import { publicationVerifiedDeploymentUrl } from '@/features/publishing/publication-release-types'
import { AskCogoSurfaceButton } from '../components/ask-cogo-surface-button'
import { useStudioLocale } from '@/i18n'
import { getStudioPublishDeployCopy } from '@/i18n/studio-publish-deploy-copy'

/**
 * Candidate preparation surface. External deployment belongs to Launch and is
 * impossible from this surface without a server-issued release artifact.
 */
export function PublishSurface() {
  const { locale } = useStudioLocale()
  const copy = getStudioPublishDeployCopy(locale).publish
  const currentProjectId = useStudioStore((state) => state.currentProjectId)
  const currentProject = useStudioStore((state) => state.currentProject)
  const projectMeta = useStudioStore((state) => state.projectMeta)
  const projectPlan = useStudioStore((state) => state.projectPlan)
  // One shared precedence with every other surface — see
  // features/project/lib/studio-active-project-plan.ts.
  const activeProjectPlan = resolveStudioActiveProjectPlan({
    storePlan: projectPlan,
    projectMeta,
    manifest: (currentProject as { manifest?: unknown } | null)?.manifest,
    projectRecord: currentProject,
  }).plan
  const designSystemRelease = activeProjectPlan?.designSystemRelease
  const publication = usePublicationRelease(currentProjectId)
  const status = projectMeta?.status ?? 'draft'
  const slug = projectMeta?.slug ?? null
  const readinessProject = withServerDatasourceReadinessProjection(
    currentProject,
    projectMeta,
    currentProjectId,
  )
  const publishGate = evaluateStudioPublishReadiness(
    readinessProject as never,
    activeProjectPlan,
    currentProjectId,
  )
  const parsedDatasourceReadinessSidecar = readProjectDatasourceReadinessSidecar(readinessProject)
  const datasourceReadinessSidecar =
    parsedDatasourceReadinessSidecar?.projectBinding.correlationStatus === 'linked' &&
    parsedDatasourceReadinessSidecar.projectBinding.projectId !== currentProjectId
      ? null
      : parsedDatasourceReadinessSidecar
  const verifiedDeploymentUrl = publicationVerifiedDeploymentUrl(publication.release)

  // This remains an editor preflight estimate. The release projection owns the
  // candidate/production verdict and can only be advanced by server artifacts.
  usePublishReadinessPublish(currentProjectId, publishGate, locale)

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 lg:p-8" data-testid="publish-surface">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {copy.eyebrow}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{copy.heading}</h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
            {copy.intro}
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <AskCogoSurfaceButton
            label={copy.askLabel}
            context={() => [
              'Studio publication candidate surface: explain editor preflight, release identity, target profile, fourteen verification axes, and exact blockers.',
              `Project=${currentProjectId ?? 'missing'}`,
              `Legacy project status=${status}`,
              `Slug=${slug ?? 'missing'}`,
              `Editor preflight=${publishGate.publishReady}`,
              `Release state=${publication.release?.derivedState ?? 'none'}`,
              `Release blockers=${publication.release?.blockers.join(' | ') || 'none'}`,
              `Design system release=${designSystemRelease?.releaseId ?? 'none'}`,
              `Design system snapshot=${designSystemRelease?.packageSnapshotHash ?? 'none'}`,
            ].join('; ')}
          />
        </div>

        <PublicationReleasePanel
          mode="candidate"
          controller={publication}
          projectId={currentProjectId}
          slug={slug}
        />

        <section className="mt-4 rounded-lg border border-border bg-card" aria-labelledby="editor-preflight-title">
          <div className="border-b border-border px-5 py-4">
            <h2 id="editor-preflight-title" className="text-sm font-semibold text-foreground">{copy.preflightHeading}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {copy.preflightDetail}
            </p>
          </div>
          <Row label={copy.savedProject}>
            <code className="font-mono text-xs text-foreground">{currentProjectId ?? '—'}</code>
          </Row>
          <Row label={copy.legacyStatus}>
            <span className="font-mono text-xs text-muted-foreground">{status}</span>
          </Row>
          <Row label={copy.slug}>
            <code className="font-mono text-xs text-foreground">{slug ?? '—'}</code>
          </Row>
          <Row label={copy.savedVersion}>
            <code className="font-mono text-xs text-foreground">v{projectMeta?.version ?? 0}</code>
          </Row>
          <Row label={copy.designSystemRelease}>
            <code className="break-all font-mono text-xs text-foreground">
              {designSystemRelease
                ? `${designSystemRelease.packageId}@${designSystemRelease.packageVersion} · ${designSystemRelease.releaseId}`
                : '—'}
            </code>
          </Row>
          <Row label={copy.designSystemSnapshot}>
            <code className="break-all font-mono text-xs text-muted-foreground">
              {designSystemRelease?.packageSnapshotHash ?? '—'}
            </code>
          </Row>
          <Row label={copy.verifiedDeployment}>
            {verifiedDeploymentUrl ? (
              <a
                href={verifiedDeploymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {verifiedDeploymentUrl}
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">{copy.noVerifiedReceipt}</span>
            )}
          </Row>
          {datasourceReadinessSidecar && (
            <div data-testid="publish-datasource-readiness-sidecar">
              <Row label={datasourceReadinessSidecar.title}>
                <div className="space-y-1">
                  <div className="font-mono text-xs text-muted-foreground">
                    {datasourceReadinessSidecar.readiness.status} / {datasourceReadinessSidecar.projectBinding.correlationStatus}
                  </div>
                  {datasourceReadinessSidecar.publishReadiness.blockers.map((blocker) => (
                    <div key={blocker} className="break-words font-mono text-[11px] text-destructive">
                      {blocker}
                    </div>
                  ))}
                </div>
              </Row>
            </div>
          )}
          <PublishGateRows publishGate={publishGate} />
          <Row label={copy.lastProjectUpdate}>
            <span className="text-xs text-muted-foreground">
              {projectMeta?.updated_at ? new Date(projectMeta.updated_at).toLocaleString() : '—'}
            </span>
          </Row>
        </section>
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-12 flex-col gap-1 border-b border-border px-5 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="min-w-0 sm:max-w-[65%] sm:text-right">{children}</div>
    </div>
  )
}
